import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Text,
  Spacer,
  Stack,
  FormRow
} from '@efast_public/fjd-component-library';
import { useNotification } from '../../shared/utils/notification/useNotification';
import { ApiRoutes, mandantClient } from '../../api/mandant-api';
import { conflictErrorMsg, ErrorType } from '../../shared/error/ErrorCode';
import {MandantDto, TenantDto} from '../../api/mandant.types';
import {isBearerTokenAuthEnabled} from "../../shared/utils/auth/auth";
import {useKeycloak} from "@react-keycloak/web";
import {LayoutSkeleton} from "../../layout/LayoutSkeleton";

interface FormData {
  mandant: string;
  email: string;
}

const MAX_ALLOWED_MANDANT_LENGTH = 32;
export function MandantPage() {
  const { t } = useTranslation('mc');
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const { keycloak, initialized } = useKeycloak();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const loginMutation = useMutation({
    mutationFn: (mandant: MandantDto) =>
      notifyUserFromRequest(
        async () => {
          mandant.mandant = `beh-${mandant.mandant}`;
          mandant.email = mandant.email.toLowerCase();
          // refresh token. This is needed because after realm is created there are claims specificially for this new realm, and we need them to send invitations
          if (isBearerTokenAuthEnabled()) {
            await mandantClient.post(ApiRoutes.mandant(), mandant);

            const isRefreshed = await keycloak.updateToken(1800);
            if (isRefreshed) {
              await mandantClient.post(ApiRoutes.mandantUser(), mandant);
            } else {
              await mandantClient.delete(ApiRoutes.mandant(), mandant);
            }
          } else {
            const newMandat: TenantDto = {
              alias: mandant.mandant,
              displayName: mandant.mandant,
              adminEmail: mandant.email,
              redirectUri: mandant.redirectUri
            }
            await mandantClient.post(ApiRoutes.tentants(), newMandat);
          }
        },
        {
          successMessage: t('api.success.createMandant'),
          loadingMessage: t('api.loading.createMandant'),
          errorMessage: t('api.error.createMandant'),
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_MANDANT_CONFLICT]: (
              reasons
            ) => {
              notifyUser({
                intent: 'error',
                title: conflictErrorMsg
              });
            }
          }
        }
      )
  });

  const onSubmit = (data: FormData) => {
    loginMutation.mutate(data);
  };

  if (!initialized) return <LayoutSkeleton />;
  if (!keycloak.authenticated && initialized) {
    keycloak.login();
  }

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '40px 20px' }}>
      <Stack direction="column" spacing="l">
        <Stack>
          <Text size="xxl" strong style={{ marginBottom: '8px' }}>
            {t('mandantPage.title')}
          </Text>

          <Text size="m">
            {t('mandantPage.description', {
              maxLength: MAX_ALLOWED_MANDANT_LENGTH
            })}
          </Text>
        </Stack>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="column" spacing="m">
            <div>
              <FormRow
                label={t('mandantPage.mandantLabel')}
                validationMessage={errors.mandant?.message}
                variant={errors.mandant ? 'error' : undefined}
              >
                <Input
                  data-testid="mandant-input"
                  type="text"
                  maxLength={MAX_ALLOWED_MANDANT_LENGTH}
                  placeholder={t('mandantPage.mandantPlaceholder')}
                  style={{ width: '100%', maxWidth: '350px' }}
                  {...register('mandant', {
                    required: t('mandantPage.mandantRequired'),
                    minLength: {
                      value: 1,
                      message: t('mandantPage.mandantMinLength')
                    },
                    pattern: {
                      value: /^[a-zA-Z0-9_-]+$/,
                      message: t('mandantPage.mandantPattern')
                    }
                  })}
                />
              </FormRow>
            </div>

            <div>
              <FormRow
                label={t('mandantPage.emailLabel')}
                validationMessage={errors.email?.message}
                variant={errors.email ? 'error' : undefined}
              >
                <Input
                  data-testid="email-input"
                  type="email"
                  placeholder={t('mandantPage.emailPlaceholder')}
                  style={{ width: '100%', maxWidth: '350px' }}
                  {...register('email', {
                    required: t('mandantPage.emailRequired'),
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: t('mandantPage.emailInvalid')
                    }
                  })}
                />
              </FormRow>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
                marginTop: '8px'
              }}
            >
              <Button
                data-testid="submit-button"
                type="submit"
                variant="primary"
                disabled={loginMutation.isPending || !isValid}
              >
                {loginMutation.isPending
                  ? t('mandantPage.submitting')
                  : t('mandantPage.submit')}
              </Button>
            </div>
          </Stack>
        </form>
      </Stack>
    </div>
  );
}
